import { formatCurrency, formatPercent } from 'configs/columns';
import { BaseFiltersConfig } from 'features/common-elements';
import { SIDE_NAV_CONFIG } from 'features/pages/portfolio/Portfolio/SideNav/SideNav.config';
import { invert } from 'lodash';
import { ASSET_CLASS_IDS } from 'types/assetClass';

import { AssetClass, ListingField } from '__generated__/globalTypes';

export type ListingColConfig<K extends keyof T, T = ListingCols> = {
  string: string;
  numeric: boolean;
  sortSelector?: K;
  display: (value: T[K]) => string | JSX.Element;
};
export type ListingColsConfig<T = ListingCols> = {
  [K in keyof T]: ListingColConfig<K, T>;
};

export interface ListingCols {
  name: string | null;
  t_current_balance_cents: Long | null;
  asset_class: AssetClass;
  loan_count: number | null;
  wa_borrower_credit_score: number | null;
  wa_coupon_bps: number | null;
  wa_dti_bps: number | null;
  wa_loan_age_months: number | null;
  wa_remaining_loan_terms_months: number | null;
  wa_ltv_bps: number | null;
}

export const listingColsConfig: ListingColsConfig = {
  [ListingField.name]: {
    string: 'Name',
    numeric: false,
    sortSelector: ListingField.name,
    display: (value: string | null) => value ?? '-',
  },
  [ListingField.t_current_balance_cents]: {
    string: 'UPB',
    numeric: true,
    sortSelector: ListingField.t_current_balance_cents,
    display: (cents: string | number | Record<string, unknown> | null) =>
      formatCurrency(cents) ?? '-',
  },
  [ListingField.asset_class]: {
    string: 'Asset Class',
    numeric: false,
    sortSelector: ListingField.asset_class,
    display: (value: string | JSX.Element) =>
      value && value !== 'HOME' ? value : value && value === 'HOME' ? 'RESI' : '-',
  },
  [ListingField.loan_count]: {
    string: '# LOANS',
    numeric: true,
    sortSelector: ListingField.loan_count,
    display: (value: number | null) => value?.toLocaleString() ?? '-',
  },
  [ListingField.wa_loan_age_months]: {
    string: 'WALA',
    numeric: true,
    sortSelector: ListingField.wa_loan_age_months,
    display: (value: number | null) => value?.toLocaleString() ?? '-',
  },
  [ListingField.wa_remaining_loan_terms_months]: {
    string: 'WAM',
    numeric: true,
    sortSelector: ListingField.wa_remaining_loan_terms_months,
    display: (value: number | null) => value?.toLocaleString() ?? '-',
  },
  [ListingField.wa_coupon_bps]: {
    string: 'GWAC',
    numeric: true,
    sortSelector: ListingField.wa_coupon_bps,
    display: (bps: number | null) => formatPercent(bps ?? 0, 3) ?? '-',
  },
  [ListingField.wa_borrower_credit_score]: {
    string: 'FICO',
    numeric: true,
    sortSelector: ListingField.wa_borrower_credit_score,
    display: (value: number | null) => value?.toString() ?? '-',
  },
  [ListingField.wa_ltv_bps]: {
    string: 'LTV',
    numeric: true,
    sortSelector: ListingField.wa_ltv_bps,
    display: (bps: number | null) => (bps ? `${((bps ?? 0) / 100).toFixed(2)}%` : '-'),
  },
  [ListingField.wa_dti_bps]: {
    string: 'DTI',
    numeric: true,
    sortSelector: ListingField.wa_dti_bps,
    display: (bps: number | null) => (bps ? (bps / 100).toFixed(2) : '-'),
  },
};

export type ListingKey = keyof typeof listingColsConfig;

export const listingColsOrder: ListingKey[] = [
  ListingField.name,
  ListingField.asset_class,
  ListingField.t_current_balance_cents,
  ListingField.loan_count,
  ListingField.wa_loan_age_months,
  ListingField.wa_remaining_loan_terms_months,
  ListingField.wa_coupon_bps,
  ListingField.wa_borrower_credit_score,
  ListingField.wa_ltv_bps,
  ListingField.wa_dti_bps,
];

export const marketplaceFiltersConfig = {
  [ListingField.has_cra]: {
    type: 'boolean',
    displayName: 'CRA',
    falseyLabel: 'Without CRA',
    truthyLabel: 'With CRA',
  },
  [ListingField.t_current_balance_cents]: {
    type: 'range',
    displayName: `total ${listingColsConfig[ListingField.t_current_balance_cents].string} (M)`,
    min: 0,
    max: 1000,
    prepare: (dollars: number) => dollars * 100 * 1000000,
  },
  [ListingField.wa_borrower_credit_score]: {
    type: 'range',
    displayName: listingColsConfig[ListingField.wa_borrower_credit_score].string,
    min: 300,
    max: 850,
    integer: true,
  },
  [ListingField.wa_coupon_bps]: {
    type: 'range',
    displayName: listingColsConfig[ListingField.wa_coupon_bps].string,
    min: 0,
    max: 25,
    prepare: (pct: number) => pct * 100,
  },
  [ListingField.wa_remaining_loan_terms_months]: {
    type: 'range',
    displayName: listingColsConfig[ListingField.wa_remaining_loan_terms_months].string,
    min: 0,
    max: 360,
  },
  [ListingField.wa_loan_age_months]: {
    type: 'range',
    displayName: listingColsConfig[ListingField.wa_loan_age_months].string,
    min: 0,
    max: 360,
  },
  [ListingField.wa_ltv_bps]: {
    type: 'range',
    displayName: listingColsConfig[ListingField.wa_ltv_bps].string,
    min: 0,
    max: 150,
    decimal: true,
    percentage: true,
    prepare: (pct: number) => pct * 100,
  },
  [ListingField.wa_dti_bps]: {
    type: 'range',
    displayName: listingColsConfig[ListingField.wa_dti_bps].string,
    min: 0,
    max: 150,
    decimal: true,
    percentage: true,
    prepare: (pct: number) => pct * 100,
  },
  [ListingField.asset_class]: {
    type: 'select-single',
    displayName: listingColsConfig[ListingField.asset_class].string,
    //TODO: we should get it from a more reasonable place
    filterOptions: SIDE_NAV_CONFIG.filter(
      (val) => !val.disabled && ASSET_CLASS_IDS[val.routeSegment] != null,
    ).map((val) => ({
      value: ASSET_CLASS_IDS[val.routeSegment] as string,
      label: val.text,
    })),
    getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
    getOptionValue: (option) => (typeof option === 'string' ? option : option.value),
    valueToString: (option) => (typeof option === 'string' ? option : (option?.label ?? '')),
  },
} as const satisfies BaseFiltersConfig;

export const filterKeyToAbbr: {
  [key in keyof typeof marketplaceFiltersConfig]: string;
} = {
  [ListingField.t_current_balance_cents]: 'UPB',
  [ListingField.wa_borrower_credit_score]: 'FICO',
  [ListingField.wa_coupon_bps]: 'GWAC',
  [ListingField.wa_remaining_loan_terms_months]: 'WAM',
  [ListingField.wa_loan_age_months]: 'WALA',
  [ListingField.wa_ltv_bps]: 'LTV',
  [ListingField.wa_dti_bps]: 'DTI',
  [ListingField.asset_class]: 'AC',
  [ListingField.has_cra]: 'CRA',
  // [ListingField.servicing_retained]: 'SR',
};

export const abbrToFilterKey = invert(filterKeyToAbbr) as {
  [key: string]: ListingField;
};
